/**
 * Compliance assessments (vfs-service src/quality/assessments, design docs/compliance-assessments.md): the words for
 * their statuses and item states, the evidence links, and the "Fix with AI" prompt. Evidence for an assessment, never
 * a certification: the wording is "evidenced", "gap", "to answer", never "compliant".
 */

export const ASSESSMENT_AREA = {
  title: 'Compliance assessments',
  icon: 'M8 1.5 13.5 4v4c0 3-2.3 5.6-5.5 6.5C4.8 13.6 2.5 11 2.5 8V4zM5.5 8l2 2 3-3.5',
  purpose: 'Reads the project against GDPR, NIS2, DORA and the EU AI Act: what the checks show, the questions your team answers, and what to fix.',
};

// The four assessments, in the order the overview's card shows them (each a gauge, off or on).
export const ASSESSMENT_FRAMEWORKS = [
  { id: 'gdpr', name: 'GDPR' },
  { id: 'nis2', name: 'NIS2' },
  { id: 'dora', name: 'DORA' },
  { id: 'aiAct', name: 'EU AI Act' },
];

/**
 * The card's four gauges: each of ASSESSMENT_FRAMEWORKS with what is known of it, from the assessments list (`all`,
 * GET /quality/assessments: every one, on or off) and the overview's summary (`on`: the ones turned on). An off one
 * whose score is not computed has none (its ring empty).
 */
export function assessmentGauges(on = [], all = []) {
  return ASSESSMENT_FRAMEWORKS.map(({ id, name }) => {
    const summary = on.find((f) => f.id === id) || null;
    const listed = all.find((f) => f.id === id) || null;
    const known = summary || listed || {};
    return {
      id,
      name: known.name || name,
      enabled: Boolean(summary || listed?.enabled),
      applies: known.applies !== false,
      score: known.score ?? null,
      gaps: known.totals ? known.totals.gap || 0 : null,
    };
  });
}

export const CONTROL_STATUS = {
  gap: { label: 'Gap', tone: 'fail' },
  todo: { label: 'To do', tone: 'info' },
  partial: { label: 'Partly evidenced', tone: 'warn' },
  evidenced: { label: 'Evidenced', tone: 'pass' },
  na: { label: 'Not applicable', tone: 'info' },
};
export const controlStatus = (status) => CONTROL_STATUS[status] || { label: status || 'Unknown', tone: 'info' };

export const ITEM_STATE = {
  pass: { label: 'Passes', tone: 'pass' },
  fail: { label: 'Fails', tone: 'fail' },
  none: { label: 'No evidence yet', tone: 'info' },
  na: { label: 'Not applicable', tone: 'info' },
  info: { label: 'Evidence', tone: 'info' },
  waived: { label: 'Accepted by answer', tone: 'pass' },
};
export const itemState = (state) => ITEM_STATE[state] || { label: state || 'Unknown', tone: 'info' };

export const PRIORITY = { blocker: 'Blocker', high: 'High', medium: 'Medium' };
export const STATUS_ORDER = ['gap', 'todo', 'partial', 'evidenced', 'na'];

/** Where an evidence link goes, in words: "services/api/a.js:12", "Data: postgres users", "Traces". */
export function evidenceWhere(e) {
  if (e.kind === 'code') { return `${e.path}${e.line ? `:${e.line}` : ''}`; }
  if (e.kind === 'data') { return `Data: ${e.store} ${e.object}`; }
  if (e.kind === 'restore') { return 'Data: Restore points'; }
  if (e.kind === 'traces') { return 'Traces'; }
  if (e.kind === 'provenance') { return 'Build provenance'; }
  return 'Quality';
}

/** The answer of a question in words. */
export function answerText(q) {
  if (!q.answer) { return 'Not answered yet'; }
  const v = q.answer.value;
  if (v === 'na') { return 'Not applicable'; }
  if (v === 'yes') { return 'Yes'; }
  if (v === 'no') { return 'No'; }
  return v;
}

/** The score's tone: pass from 85, warn from 50, else fail; none while nothing applies. */
export function assessmentTone(score) {
  if (score === null || score === undefined) { return 'info'; }
  if (score >= 85) { return 'pass'; }
  if (score >= 50) { return 'warn'; }
  return 'fail';
}

/**
 * "Fix with AI" on a control: what fails, where, how to fix it, the checks to run again and the assessment to read to
 * confirm; the open questions are listed for the person, never answered by the AI.
 */
export function assessmentFixPrompt(framework, control) {
  const lines = [`Fix the ${framework.name} gap "${control.ref} ${control.title}" in this project (compliance assessment in the Quality tab).`];
  if (control.why) { lines.push(`Why it matters: ${control.why}`); }
  const failing = (control.checks || []).filter((c) => c.state === 'fail');
  const checks = new Set();
  if (failing.length) {
    lines.push('', 'What fails:');
    failing.forEach((c) => {
      lines.push(`- ${c.label}: ${c.detail}`);
      (c.fixes || []).slice(0, 8).forEach((f) => { if (f.where) { lines.push(`  - ${f.where}${f.fix ? `: ${f.fix}` : ''}`); } });
      if (!(c.fixes || []).length && c.fix) { lines.push(`  Fix: ${c.fix}`); }
      if (c.check) { checks.add(c.check); }
    });
  }
  const notRun = (control.checks || []).filter((c) => c.notRun).map((c) => c.notRun);
  notRun.forEach((id) => checks.add(id));
  if (control.fix) { lines.push('', `How to fix it: ${control.fix}`); }
  lines.push('', 'Change only what this needs.');
  if (checks.size) { lines.push(`Then run the Quality checks ${[...checks].join(', ')} again (vfs_quality_run, then vfs_quality_status).`); }
  lines.push(`Then read the assessment again to confirm: vfs_quality_assessment with framework "${framework.id}" and control "${control.id}".`);
  const open = (control.questions || []).filter((q) => q.state === 'none' || q.state === 'fail');
  if (open.length) {
    lines.push('', 'Do not answer these questions yourself: they are mine to answer in the Quality tab. List them for me at the end, with what you found that helps me answer:');
    open.forEach((q) => lines.push(`- ${q.text}${q.answer ? ` (now: ${answerText(q)})` : ''}`));
  }
  return lines.join('\n');
}

/** The answers a person changed in a control's form, ready to send: `[{ question, value, note }]`. */
export function changedAnswers(questions, draft) {
  return (questions || []).flatMap((q) => {
    const d = draft[q.id];
    if (!d) { return []; }
    const value = d.value === '' ? null : d.value;
    const before = q.answer || {};
    if ((value ?? null) === (before.value ?? null) && (d.note || '') === (before.note || '')) { return []; }
    return [{ question: q.id, value, ...(d.note ? { note: d.note } : {}) }];
  });
}
